import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { ToasterMessageService } from '../../toaster-message/toaster-message.service';
import { InvoiceService } from '../../../data/service/invoice.service';
import { take } from 'rxjs/operators';
import { AppConfigService } from 'src/app/appConfig.service';

@Component({
  selector: 'app-suggested-matches-modal',
  templateUrl: './suggested-matches-modal.component.html',
  styleUrls: ['./suggested-matches-modal.component.scss']
})
export class SuggestedMatchesModalComponent implements OnInit, OnChanges {
  @Input() show = false;
  @Input() row: any;
  @Output() close = new EventEmitter<void>();
  @Output() matchSelected = new EventEmitter<object>();

  matchData: any[] = [];
  isLoading = false;
  errorMessage = '';
  hasInitializedData = false;

  selectedIndex: number | null = null;

  // Use sessionStorage as a fallback for organization ID
  private get organizationId(): string {
    return sessionStorage.organizationId || localStorage.getItem('organizationId') || '';
  }

  constructor(
    private _http: HttpClient,
    private _toasterService: ToasterMessageService,
    private _invoiceService: InvoiceService,
    private configService: AppConfigService
  ) { }

  ngOnInit() {
    // Since the component is created fresh each time (due to *ngIf),
    // we should always load data on initialization if we have a row
    this.loadDataIfNeeded();
  }

  ngOnChanges(changes: SimpleChanges) {
    this.loadDataIfNeeded();
  }

  /**
   * Helper method to load data if we have a row and haven't loaded data yet
   */
  private loadDataIfNeeded() {
    if (this.row && !this.hasInitializedData) {
      // If the row is an object with nested data, try to extract it
      if (this.row && typeof this.row === 'object' && !Array.isArray(this.row)) {
        // Log all properties to help with debugging
      }

      this.loadMatchData();
      this.hasInitializedData = true;
    }
  }

  loadMatchData() {
    // Try different properties that might contain the related ID (could be transaction ID or unreconciledReportId)
    // Search through all nested properties recursively to find any ID-like field
    const possibleIdKeys = ['id', 'unreconciledReportId', 'transactionId', 'reportId'];
    let relatedId = null;

    // First try direct properties
    for (const key of possibleIdKeys) {
      if (this.row && this.row[key] && typeof this.row[key] === 'string' && this.row[key].length > 0) {
        relatedId = this.row[key];
        break;
      }
    }

    // If still no ID found, check if the row itself is a string ID
    if (!relatedId && typeof this.row === 'string' && this.row.length > 0) {
      relatedId = this.row;
    }

    if (!relatedId) {
      console.error('Cannot fetch match data: Missing related ID', this.row);
      this.errorMessage = 'Cannot fetch match data: Missing related ID';
      return;
    }

    if (!this.organizationId) {
      console.error('Cannot fetch match data: Missing organization ID');
      this.errorMessage = 'Cannot fetch match data: Missing organization ID';
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';

    // Construct API URL to get match data with the correct parameters
    const apiUrl = `${this.configService.apiURL}/Invoices/getMatchData?organizationId=${this.organizationId}&relatedId=${relatedId}`;
    console.log('API URL:', apiUrl);

    this._http.get(apiUrl).subscribe(
      (response: any) => {
        this.isLoading = false;

        if (Array.isArray(response)) {
          this.matchData = response;
        } else if (response && Array.isArray(response.data)) {
          this.matchData = response.data;
        } else {
          this.matchData = [];
          // Don't set error message for empty results, we'll show a "No matches found" message instead
        }

        // Clear any previous error message if we got a successful response
        this.errorMessage = '';
      },
      (error) => {
        this.isLoading = false;
        this.errorMessage = 'Error loading match data. Please try again.';
        this._toasterService.displayToasterState('error', 'Error', 'Failed to load suggested matches');
        console.error('Error fetching match data:', error);
      }
    );
  }

  onClose() {
    this.close.emit();
  }

  // Close modal when clicking on backdrop
  onBackdropClick(event: MouseEvent) {
    // Make sure we're clicking directly on the backdrop, not a child element
    if (event.target === event.currentTarget) {
      this.onClose();
    }
  }

  // Format currency value
  formatCurrency(value: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  }

  // Format date as DD/MM/YYYY
  formatDate(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString; // If invalid date, return the original string
    return date.toLocaleDateString('en-GB');
  }

  // Handle card selection (highlights the selected card and dims others)
  onSelectCard(event: any) {
    // Always select the clicked card (deselection happens by selecting another card)
    this.selectedIndex = event.index;
  }

  // Clear selection when clicking outside any card
  clearSelection() {
    this.selectedIndex = null;
  }

  // Confirm match (after selection)
  confirmMatch() {
    if (this.selectedIndex == null || !this.matchData[this.selectedIndex]) return;
    const match = this.matchData[this.selectedIndex];
    if (!this.row || !match) {
      this._toasterService.displayToasterState('error', 'Error', 'Missing data for match operation');
      return;
    }
    const unreconciledReportId = this.row.id || this.row.unreconciledReportId || this.row.transactionId || this.row.reportId;
    if (!unreconciledReportId) {
      this._toasterService.displayToasterState('error', 'Error', 'Missing transaction ID');
      console.error('Cannot match: Missing unreconciledReportId', this.row);
      return;
    }
    if (!match.id) {
      this._toasterService.displayToasterState('error', 'Error', 'Missing invoice ID');
      console.error('Cannot match: Missing invoiceId', match);
      return;
    }
    const model = {
      unreconciledReportIds: unreconciledReportId,
      invoiceId: match.id,
      organizationId: this.organizationId
    };
    this._invoiceService
      .updateSuggestedMatches(model)
      .pipe(take(1))
      .subscribe(
        (response) => {
          this._toasterService.displayToasterState('success', 'Success', 'Match successfully applied');
          this.matchSelected.emit(new Object({ unRecId: unreconciledReportId, relatedId: match.id, invoiceUrl: match.url, accountName: match.accountName }));
          this.onClose();
        },
        (error) => {
          this._toasterService.displayToasterState('error', 'Error', error?.error?.errorMessage || 'Failed to apply match');
        }
      );
  }

  getTooltipText(): string {
    if (this.selectedIndex != null && !this.matchData[this.selectedIndex].required_Fields) {
      return 'Please fill in all required fields in invoice before confirming.';
    }
    return '';
  }

  showTooltip(): boolean {
    return this.selectedIndex != null && !this.matchData[this.selectedIndex].required_Fields;
  }
}
